import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import {
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
} from "@opentelemetry/sdk-trace-base";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { openobserveRum } from "@openobserve/browser-rum";
import { openobserveLogs } from "@openobserve/browser-logs";
import { env } from "@/lib/env";
import {
  OBSERVABILITY_NOISE_URL_PATTERNS,
  isObservabilityNoise,
  isObservabilityNoiseUrl,
} from "@/lib/is-observability-noise";

const SERVICE_NAME = "sked-link-admin";
const RUM_SITE = "ooingest.skedsocial.com";
const APP_VERSION = env.appVersion || "unknown";

export function setObservabilityUser(user: {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  emailVerified?: boolean;
  selectedAccount?: string;
  accounts?: string[];
}) {
  const userContext = {
    id: user.uid,
    name: user.displayName || user.email || user.uid,
    email: user.email || undefined,
    uid: user.uid,
    selectedAccount: user.selectedAccount,
    accountsCount: user.accounts?.length,
    emailVerified: user.emailVerified,
  };

  const globalContext = {
    appVersion: APP_VERSION,
    selectedAccount: user.selectedAccount,
    accountsCount: user.accounts?.length,
    emailVerified: user.emailVerified,
  };

  openobserveRum.setUser(userContext);
  openobserveLogs.setUser(userContext);
  openobserveRum.setGlobalContext(globalContext);
  openobserveLogs.setGlobalContext(globalContext);
}

export function clearObservabilityUser() {
  openobserveRum.clearUser();
  openobserveLogs.clearUser();
  openobserveRum.clearGlobalContext();
  openobserveLogs.clearGlobalContext();
}

export function initObservability() {
  if (env.otelEnabled !== "true") {
    console.info("[Observability] disabled");
    return;
  }

  const environment = env.mode || "development";
  const sampleRate = parseFloat(env.otelSampleRate || "0");
  const otelEndpoint = env.otelEndpoint || "";
  const otelAuthHeader = env.otelAuthHeader || "";
  const otelOrg = env.otelOrg || "";
  const otelStream = env.otelStream || "default";

  // --- OpenTelemetry traces ---
  const exporter = new OTLPTraceExporter({
    url: otelEndpoint,
    headers: {
      Authorization: otelAuthHeader,
      organization: otelOrg,
      "stream-name": otelStream,
    },
  });

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: SERVICE_NAME,
      "deployment.environment.name": environment,
    }),
    sampler: new TraceIdRatioBasedSampler(sampleRate),
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  provider.register();

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [/\.skedsocial\.com/, /\.sked\.link/],
        ignoreUrls: [...OBSERVABILITY_NOISE_URL_PATTERNS],
        ignoreNetworkEvents: true,
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: [/\.skedsocial\.com/, /\.sked\.link/],
        ignoreUrls: [...OBSERVABILITY_NOISE_URL_PATTERNS],
        ignoreNetworkEvents: true,
      }),
      new DocumentLoadInstrumentation(),
    ],
  });

  console.log(
    `[Observability] OpenTelemetry initialized (sample rate: ${sampleRate * 100}%)`
  );

  // --- OpenObserve RUM ---
  const rumClientToken = env.rumClientToken || "";
  const rumOrg = env.rumOrg || "";
  const rumSessionSampleRate = parseInt(env.rumSessionSampleRate || "100", 10);
  const rumSessionReplaySampleRate = parseInt(
    env.rumSessionReplaySampleRate || "0",
    10
  );

  openobserveRum.init({
    applicationId: SERVICE_NAME,
    clientToken: rumClientToken,
    site: RUM_SITE,
    organizationIdentifier: rumOrg,
    service: SERVICE_NAME,
    env: environment,
    version: APP_VERSION,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    apiVersion: "v1",
    insecureHTTP: false,
    defaultPrivacyLevel: "mask-user-input",
    sessionSampleRate: rumSessionSampleRate,
    sessionReplaySampleRate: rumSessionReplaySampleRate,
    beforeSend: (event) => {
      if (event.type === "resource") {
        return !isObservabilityNoiseUrl(event.resource.url || "");
      }

      if (event.type === "error") {
        return !isObservabilityNoise(
          event.error.message,
          event.error.stack,
          event.error.resource?.url
        );
      }

      return true;
    },
  });

  // --- OpenObserve Logs ---
  openobserveLogs.init({
    clientToken: rumClientToken,
    site: RUM_SITE,
    organizationIdentifier: rumOrg,
    service: SERVICE_NAME,
    env: environment,
    version: APP_VERSION,
    forwardErrorsToLogs: true,
    insecureHTTP: false,
    apiVersion: "v1",
    beforeSend: (event) =>
      !isObservabilityNoise(
        event.message,
        event.error?.message,
        event.error?.stack,
        event.http?.url
      ),
  });

  if (rumSessionReplaySampleRate > 0) {
    openobserveRum.startSessionReplayRecording();
  }

  console.log("[Observability] OpenObserve RUM & Logs initialized");
}
